'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type ColorMode = 'dark' | 'light';
export type Palette   = 'default' | 'heroic' | 'ocean';

interface ThemeContextValue {
  theme: ColorMode;
  colorMode: ColorMode;
  toggle: () => void;
  palette: Palette;
  setPalette: (p: Palette) => void;
}

const THEME_COLORS: Record<Palette, Record<ColorMode, string>> = {
  default: { light: '#ffffff', dark: '#0f0f0f' },
  heroic:  { light: '#f3fcf0', dark: '#0d1a0c' },
  ocean:   { light: '#f0f4ff', dark: '#080e1c' },
};

function applyThemeColor(palette: Palette, mode: ColorMode) {
  const color = THEME_COLORS[palette][mode];
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = color;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  colorMode: 'light',
  toggle: () => {},
  palette: 'heroic',
  setPalette: () => {},
});

interface ThemeProviderProps {
  children: React.ReactNode;
  initialMode?: ColorMode;
  initialPalette?: Palette;
}

export function ThemeProvider({ children, initialMode = 'light', initialPalette = 'heroic' }: ThemeProviderProps) {
  const [colorMode, setColorMode] = useState<ColorMode>(initialMode);
  const [palette,   setPaletteState] = useState<Palette>(initialPalette);

  // On mount: apply server values to DOM and sync localStorage
  useEffect(() => {
    document.documentElement.classList.toggle('dark', initialMode === 'dark');
    document.documentElement.setAttribute('data-theme', initialPalette);
    localStorage.setItem('theme', initialMode);
    localStorage.setItem('theme-palette', initialPalette);
    applyThemeColor(initialPalette, initialMode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveToServer = useCallback((mode: ColorMode, pal: Palette) => {
    fetch('/api/appearance', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, palette: pal }),
    }).catch(() => {});
  }, []);

  function toggle() {
    setColorMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      document.documentElement.classList.toggle('dark', next === 'dark');
      applyThemeColor(palette, next);
      saveToServer(next, palette);
      return next;
    });
  }

  function setPalette(p: Palette) {
    setPaletteState(p);
    localStorage.setItem('theme-palette', p);
    document.documentElement.setAttribute('data-theme', p);
    applyThemeColor(p, colorMode);
    saveToServer(colorMode, p);
  }

  return (
    <ThemeContext.Provider value={{ theme: colorMode, colorMode, toggle, palette, setPalette }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
